import useAutocomplete from '@material-ui/lab/useAutocomplete';
import debounce from 'lodash/debounce';
import React, { createContext, useContext, useRef } from 'react';

import { useLocationSearchAutocompleteOptions } from '@/hooks/useLocationSearchAutocompleteOptions';
import { MAX_RECENT_SEARCHES } from '@/hooks/useRecentSearchesWithFilters';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import { IGroupOption, IOption, limitRecentSearches } from '@/utility/autoCompleteGroupedOptions';
import { stripDiacritics } from '@/utility/stripDiacritics';
import {
  LocationGroupType,
  mapLocationLabelToGroupName,
  setSurfacedLocation,
} from '@/utility/surfacedLocation';

import { EHomeSearchWidgetVariant } from './HomeSearchWidget';
import { useLocationFilterContext } from './LocationFilterContext';

type TLocationAutocompleteContext = {
  isOpened: boolean;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  menuProps: React.HTMLAttributes<HTMLDivElement>;
  allGroupedOptions: IGroupOption[];
  groupedOptions: IGroupOption[];
  getOptionProps: ({
    option,
    index,
  }: {
    option: IOption;
    index: number;
  }) => React.HTMLAttributes<React.ElementType>;
};

const LocationAutocompleteContext = createContext<TLocationAutocompleteContext>({
  isOpened: false,
  inputProps: {},
  menuProps: {},
  allGroupedOptions: [],
  groupedOptions: [],
  getOptionProps: () => ({}),
});

export const useLocationAutocompleteContext = () => useContext(LocationAutocompleteContext);

type TLocationAutocompleteProviderProps = {
  variant: EHomeSearchWidgetVariant;
  isOpened: boolean;
  onOpen: () => void;
  onDataSelection?: () => void;
  onTrackFilterToggled?: (
    oldSelection: Record<string, unknown>,
    newSelection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => void;
};

export const LocationAutocompleteProvider: React.FC<
  React.PropsWithChildren<TLocationAutocompleteProviderProps>
> = ({ variant, isOpened, onOpen, onDataSelection, onTrackFilterToggled, children }) => {
  const {
    addressValue,
    setAddressValue,
    suggestions,
    recentSearches,
    popularDestinations,
    navigateToRecentSearch,
    setMapboxValues,
  } = useLocationFilterContext();

  const debouncedTrackFilterToggledRef = useRef(
    debounce((oldValue: string, newValue: string) => {
      onTrackFilterToggled?.(
        {
          location: oldValue,
        },
        {
          location: newValue,
        },
        ESearchWidgetFilter.Location,
      );
    }, 500),
  );

  const { options, optionGroupLabels } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: false,
    suggestions,
    recentSearches,
    popularDestinations,
    includeRecentSearches:
      variant !== EHomeSearchWidgetVariant.AutoRentals &&
      variant !== EHomeSearchWidgetVariant.Escapes,
    includePopularDestinations: variant !== EHomeSearchWidgetVariant.AutoRentals,
  });

  const handleAddressChange = (value: string, reason: string) => {
    if (reason === 'input') {
      // reset mapbox values when the user is typing/manually entering an address
      setMapboxValues('', '');

      // on address change, debounce the tracking to not spam analytics
      debouncedTrackFilterToggledRef.current?.(addressValue, value);

      setAddressValue(value);
    }

    setSurfacedLocation(LocationGroupType.OTHER);
  };

  const handleAddressSelect = async (option: IOption) => {
    if (!option) return;
    const { label, value, url, groupName } = option;

    if (url) {
      navigateToRecentSearch(url);
      return;
    }

    if (value?.mapbox_id) {
      const fullAddress = await retrieveMapboxFullAddress(value);
      setMapboxValues(value.mapbox_id, fullAddress);
    }

    if (value?.name) {
      setAddressValue(value.name);
    } else if (value?.address) {
      setAddressValue(value.address);
    } else if (label) {
      setAddressValue(label);
    }

    setSurfacedLocation(mapLocationLabelToGroupName(groupName));
    onDataSelection?.();

    if (label) {
      onTrackFilterToggled?.(
        {
          location: addressValue,
        },
        {
          location: label,
        },
        ESearchWidgetFilter.Location,
      );
    }
  };

  const { getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
    inputValue: addressValue,
    options,
    filterOptions: (autocompleteOptions, autocompleteState) => {
      const input = stripDiacritics(autocompleteState.inputValue.trim().toLowerCase());

      if (input.length <= 2) {
        return autocompleteOptions;
      }

      return autocompleteOptions.filter(autocompleteOption => {
        // Filter everything except suggestions
        if (autocompleteOption.groupName === optionGroupLabels.suggestions) {
          return autocompleteOption;
        }

        const candidate = stripDiacritics(
          autocompleteState.getOptionLabel(autocompleteOption).trim().toLowerCase(),
        );

        return candidate.indexOf(input) > -1;
      });
    },
    getOptionLabel: option => option.label,
    groupBy: option => option.groupName,
    onInputChange: (_, value, reason) => handleAddressChange(value, reason),
    onChange: (_, value) => handleAddressSelect(value as unknown as IOption),
    onOpen: onOpen,
    freeSolo: true,
    open: isOpened,
  });

  return (
    <LocationAutocompleteContext.Provider
      value={{
        isOpened,
        inputProps: getInputProps(),
        menuProps: getListboxProps(),
        allGroupedOptions: groupedOptions as unknown as IGroupOption[],
        groupedOptions: limitRecentSearches(
          groupedOptions as unknown as IGroupOption[],
          optionGroupLabels.recentSearches,
          MAX_RECENT_SEARCHES,
        ),
        getOptionProps,
      }}>
      {children}
    </LocationAutocompleteContext.Provider>
  );
};
